import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { CompanionPlant } from '../../types/plant';
import { Info, Plus, Loader2, Check } from 'lucide-react';
import { useGarden } from '../../hooks/useGarden';
import { useAuth } from '../../hooks/useAuth';
import GardenLoginModal from './GardenLoginModal';

interface CompanionPlantPreviewProps {
  companion: CompanionPlant;
  onClose: () => void;
  isInGarden?: boolean;
  onPlantClick: () => void;
}

export default function CompanionPlantPreview({ companion, onClose, isInGarden = false, onPlantClick }: CompanionPlantPreviewProps) {
  const plant = companion.companion_plant || companion.plant;
  const { user } = useAuth();
  const { addToGarden, loading } = useGarden();
  const [error, setError] = useState<string | null>(null);
  const [isAdded, setIsAdded] = useState(isInGarden);
  const [showLoginModal, setShowLoginModal] = useState(false);

  if (!plant) return null;

  const handleAddToGarden = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent tile click when clicking the button
    setError(null);
    
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    
    try {
      await addToGarden(plant.id);
      setIsAdded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add to garden');
    }
  };

  return (
    <>
      <div 
        className="flex gap-3 p-3 rounded-lg border hover:border-primary-200 hover:bg-primary-50 transition-colors cursor-pointer"
        onClick={onPlantClick}
      >
        <div className="flex gap-3 flex-1">
          <img
            src={plant.image_url}
            alt={plant.name}
            className="w-16 h-16 rounded-lg object-cover"
          />
          <div className="flex-1">
            <div className="block">
              <h4 className="font-medium text-sm">{plant.name}</h4>
              <p className="text-xs text-gray-500 italic">{plant.scientific_name}</p>
            </div>
            {companion.benefits && (
              <div className="flex items-start gap-1.5 mt-1">
                <Info className="h-3 w-3 text-primary-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-gray-600 line-clamp-2">{companion.benefits}</p>
              </div>
            )}
            {error && (
              <p className="text-xs text-red-600 mt-1">{error}</p>
            )}
          </div>
        </div>
        {!isAdded ? (
          <button
            onClick={handleAddToGarden}
            disabled={loading}
            className="flex-shrink-0 p-2 text-primary-500 hover:text-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Add to My Garden"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Plus className="h-5 w-5" />
            )}
          </button>
        ) : (
          <div
            className="flex-shrink-0 p-2 text-primary-500"
            title="Added to Garden"
          >
            <Check className="h-5 w-5" />
          </div>
        )}
      </div>

      <GardenLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        plantName={plant.name}
      />
    </>
  );
}